/**
 * Auth API Service
 * Cognito-backed authentication using amazon-cognito-identity-js
 * Falls back to local API calls when Cognito is not configured (development)
 */

import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserAttribute,
  AuthenticationDetails,
  CognitoUserSession,
  ISignUpResult,
} from 'amazon-cognito-identity-js';
import { COGNITO_CONFIG, isCognitoConfigured } from '../config/cognito';
import type { LoginResponse, RegisterResponse, RefreshResponse } from '../types/auth';
import type { ApiErrorBody } from './planApi';

const API_BASE_URL = '/api/auth';

export class AuthApiError extends Error {
  public errorCode?: string;
  constructor(public status: number, message: string, errorCode?: string) {
    super(message);
    this.name = 'AuthApiError';
    this.errorCode = errorCode;
  }
}

// --- Cognito User Pool (lazy-initialized) ---

let userPool: CognitoUserPool | null = null;

function getUserPool(): CognitoUserPool {
  if (!userPool) {
    userPool = new CognitoUserPool({
      UserPoolId: COGNITO_CONFIG.UserPoolId,
      ClientId: COGNITO_CONFIG.ClientId,
    });
  }
  return userPool;
}

function getCognitoUser(email: string): CognitoUser {
  return new CognitoUser({
    Username: email,
    Pool: getUserPool(),
  });
}

// --- Local API helpers (used when Cognito is not configured) ---

async function handleLocalResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    // `?? null` and `?.` throughout: a gateway can send a literal `null` body,
    // which parses successfully — so `.catch()` never fires and a direct
    // property read throws a TypeError out of the error path itself. Same
    // hazard as planApi/adminApi; this file is exempt from the unsafe-* rules,
    // so nothing flagged it here.
    const error = await response.json().catch(() => null) as ApiErrorBody | null;
    throw new AuthApiError(response.status, error?.message || error?.error || 'Request failed', error?.error);
  }
  return response.json();
}

// --- Public API ---

/**
 * Request beta access (email-based invite flow)
 */
export async function requestBetaAccess(email: string): Promise<{ success: boolean }> {
  const response = await fetch('/api/beta/request', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return handleLocalResponse<{ success: boolean }>(response);
}

/**
 * Validate a beta invite key
 */
export async function validateBetaKey(key: string): Promise<{ valid: boolean; email?: string }> {
  const response = await fetch(`/api/beta/validate?key=${encodeURIComponent(key)}`);
  return handleLocalResponse<{ valid: boolean; email?: string }>(response);
}

/**
 * Register a new user
 */
export async function register(
  email: string,
  password: string,
  betaKey?: string,
): Promise<RegisterResponse> {
  if (!isCognitoConfigured()) {
    const response = await fetch(`${API_BASE_URL}/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, betaKey }),
    });
    return handleLocalResponse<RegisterResponse>(response);
  }

  return new Promise<RegisterResponse>((resolve, reject) => {
    const validationData = betaKey
      ? [new CognitoUserAttribute({ Name: 'betaKey', Value: betaKey })]
      : [];
    getUserPool().signUp(email, password, [], validationData, (err, result?: ISignUpResult) => {
      if (err) {
        const message = err.message || 'Registration failed';
        if (message.includes('User already exists') || message.includes('UsernameExistsException')) {
          reject(new AuthApiError(409, 'An account with this email already exists'));
          return;
        }
        // Normalise Pre Sign-Up Lambda rejections — Cognito wraps these as
        // "PreSignUp failed with error <lambda message>."
        if (message.includes('Invalid or expired beta access key')) {
          reject(new AuthApiError(403, 'Your invitation link is invalid or has expired. Please request a new one.'));
          return;
        }
        if (message.includes('Beta access key is required')) {
          reject(new AuthApiError(403, 'A valid invitation is required to create an account.'));
          return;
        }
        if (message.includes('Registration is temporarily unavailable')) {
          reject(new AuthApiError(503, 'Registration is temporarily unavailable. Please try again in a few minutes.'));
          return;
        }
        reject(new AuthApiError(400, message));
        return;
      }

      resolve({
        userId: result?.userSub || '',
        requiresVerification: !result?.userConfirmed,
        message: result?.userConfirmed
          ? 'Account created successfully.'
          : 'Verification code sent. Please check your email.',
      });
    });
  });
}

/**
 * Confirm sign-up with 6-digit verification code
 */
export async function confirmSignUp(email: string, code: string): Promise<void> {
  if (!isCognitoConfigured()) {
    throw new AuthApiError(501, 'Code verification not available in local mode');
  }

  const cognitoUser = getCognitoUser(email);

  return new Promise<void>((resolve, reject) => {
    cognitoUser.confirmRegistration(code, true, (err) => {
      if (err) {
        const message = err.message || 'Verification failed';
        if (message.includes('CodeMismatchException') || message.includes('Invalid verification code')) {
          reject(new AuthApiError(400, 'Invalid verification code. Please try again.'));
          return;
        }
        if (message.includes('ExpiredCodeException')) {
          reject(new AuthApiError(400, 'Verification code has expired. Please request a new one.'));
          return;
        }
        reject(new AuthApiError(400, message));
        return;
      }
      resolve();
    });
  });
}

/**
 * Resend confirmation code
 */
export async function resendVerification(email: string): Promise<{ message: string }> {
  if (!isCognitoConfigured()) {
    const response = await fetch(`${API_BASE_URL}/resend-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    return handleLocalResponse<{ message: string }>(response);
  }

  const cognitoUser = getCognitoUser(email);

  return new Promise<{ message: string }>((resolve, reject) => {
    cognitoUser.resendConfirmationCode((err) => {
      if (err) {
        reject(new AuthApiError(400, err.message || 'Failed to resend code'));
        return;
      }
      resolve({ message: 'Verification code resent.' });
    });
  });
}

/**
 * Login with email and password
 */
export async function login(email: string, password: string): Promise<LoginResponse> {
  if (!isCognitoConfigured()) {
    const response = await fetch(`${API_BASE_URL}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    return handleLocalResponse<LoginResponse>(response);
  }

  const cognitoUser = getCognitoUser(email);
  const authDetails = new AuthenticationDetails({
    Username: email,
    Password: password,
  });

  return new Promise<LoginResponse>((resolve, reject) => {
    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session: CognitoUserSession) => {
        // Use ID token (not access token) — it contains email + sub claims
        // needed by the server's auth middleware for user resolution
        resolve({
          accessToken: session.getIdToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
          user: {
            id: session.getIdToken().payload.sub,
            email,
          },
        });
      },
      onFailure: (err) => {
        const message = err.message || 'Login failed';
        if (message.includes('Incorrect username or password') || message.includes('NotAuthorizedException')) {
          reject(new AuthApiError(401, 'Invalid email or password'));
          return;
        }
        if (message.includes('User is not confirmed') || message.includes('UserNotConfirmedException')) {
          reject(new AuthApiError(403, 'Please verify your email address before logging in.', 'email_not_verified'));
          return;
        }
        reject(new AuthApiError(401, message));
      },
    });
  });
}

/**
 * Refresh access token using Cognito SDK session management.
 */
export async function refreshAccessToken(): Promise<RefreshResponse> {
  if (!isCognitoConfigured()) {
    throw new AuthApiError(501, 'Token refresh not available in local mode without refresh token');
  }

  const currentUser = getUserPool().getCurrentUser();
  if (!currentUser) {
    throw new AuthApiError(401, 'No current user session');
  }

  return new Promise<RefreshResponse>((resolve, reject) => {
    currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        reject(new AuthApiError(401, 'Session expired. Please log in again.'));
        return;
      }
      resolve({
        accessToken: session.getIdToken().getJwtToken(),
      });
    });
  });
}

/**
 * Refresh access token with an explicit refresh token (for local auth fallback)
 */
export async function refreshAccessTokenLocal(refreshToken: string): Promise<RefreshResponse> {
  const response = await fetch(`${API_BASE_URL}/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  return handleLocalResponse<RefreshResponse>(response);
}

/**
 * Verify email with magic link token (local auth only)
 */
export async function verifyEmail(token: string): Promise<{ message: string; userId: string }> {
  const response = await fetch(`${API_BASE_URL}/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  return handleLocalResponse<{ message: string; userId: string }>(response);
}

/**
 * Request a password reset code (Cognito sends email via Resend Lambda)
 */
export async function forgotPassword(email: string): Promise<void> {
  if (!isCognitoConfigured()) {
    throw new AuthApiError(501, 'Password reset not available in local mode');
  }

  const cognitoUser = getCognitoUser(email);

  return new Promise<void>((resolve, reject) => {
    cognitoUser.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => {
        const message = err.message || 'Failed to send reset code';
        if (message.includes('LimitExceededException')) {
          reject(new AuthApiError(429, 'Too many attempts. Please wait before trying again.'));
          return;
        }
        // Don't reveal whether account exists — resolve silently for UserNotFoundException
        if (message.includes('UserNotFoundException') || message.includes('User does not exist')) {
          resolve();
          return;
        }
        reject(new AuthApiError(400, message));
      },
      inputVerificationCode: () => resolve(),
    });
  });
}

/**
 * Confirm password reset with verification code and new password
 */
export async function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  if (!isCognitoConfigured()) {
    throw new AuthApiError(501, 'Password reset not available in local mode');
  }

  const cognitoUser = getCognitoUser(email);

  return new Promise<void>((resolve, reject) => {
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => {
        const message = err.message || 'Password reset failed';
        if (message.includes('CodeMismatchException') || message.includes('Invalid verification code')) {
          reject(new AuthApiError(400, 'Invalid verification code. Please try again.'));
          return;
        }
        if (message.includes('ExpiredCodeException')) {
          reject(new AuthApiError(400, 'Verification code has expired. Please request a new one.'));
          return;
        }
        if (message.includes('InvalidPasswordException') || message.includes('Password does not conform')) {
          reject(new AuthApiError(400, 'Password does not meet requirements. Please choose a stronger password.'));
          return;
        }
        if (message.includes('LimitExceededException')) {
          reject(new AuthApiError(429, 'Too many attempts. Please wait before trying again.'));
          return;
        }
        if (message.includes('NotAuthorizedException')) {
          reject(new AuthApiError(400, 'This reset link is no longer valid. Please request a new one.'));
          return;
        }
        reject(new AuthApiError(400, message));
      },
    });
  });
}

/**
 * Logout
 */
export async function logout(refreshToken: string | null): Promise<void> {
  if (!isCognitoConfigured()) {
    await fetch(`${API_BASE_URL}/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    return;
  }

  const currentUser = getUserPool().getCurrentUser();
  if (currentUser) {
    currentUser.signOut();
  }
}
